/*
 * Copyright (c) 2022 CalAmp Corp. All Rights Reserved
 */
import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo
} from 'react';
import classnames from 'classnames';
import {translate} from '@ion/core/src/locale/locale';
import {BulkActionEntity, GenericObject} from '@ion/core/src/types/entities';
import {isValue} from '@ion/core/src/utils/StringUtil';
import {FormItem} from '@ion/dls/src/components/forms/FormItem';
import {
  useEditorStyles
} from '@ion/dls/src/hooks/useEditorStyles';
import {
  FormState
} from '@ion/dls/src/hooks/useFormState';
import useFormWithValidation from '@ion/dls/src/hooks/useFormWithValidation';
import {BulkActionProps} from '@ion/dls/src/imports/BulkUploadContainer';
import {
  arePastedIdsValid,
  BulkSelectEntitiesFormFields,
  serialize
} from './BulkSelectEntitiesEditorUtils';
import EntityBulkUpload from './EntityBulkUpload';
import IdentifierTypeChooser from './IdentifierTypeChooser';
import {useStyles} from './Step';

export interface EntitySelectionStepProps extends BulkActionProps {
  formState: FormState;
  hasFile: boolean;
  entity: BulkActionEntity;
  identifierTypes: string[];
  showFileOption?: boolean;
  setFormState: (updatedFormState: FormState) => void;
  onValidationSuccess: (serializedFormState: GenericObject) => void;
}

/**
 * This step will display the page for uploading or pasting identifiers.
 */
export const EntitySelectionStep = forwardRef(({
  entity,
  identifierTypes,
  formState,
  hasFile,
  setFormState,
  onValidationSuccess,
  isBulkAction = false,
  showFileOption = true
}: EntitySelectionStepProps, ref) => {
  const localClasses = useStyles();
  const {classes}    = useEditorStyles();
  const entityLabel  = translate('common:entityLabels', {entity, returnObjects: true});
  const {shared}     = translate('common:editors', {entity: entityLabel[entity], returnObjects: true});

  const validate = useMemo(() => ({
    [BulkSelectEntitiesFormFields.EntityIdentifiers]    : ({value}) => isValidSelection(value, hasFile, entity),
    [BulkSelectEntitiesFormFields.EntityIdentifierType] : ({value}) => value?.label
  }), [hasFile]);

  const {onSubmit} = useFormWithValidation({
    validate,
    serialize,
    onValidationSuccess,
    onValidationFailure : setFormState
  });

  useImperativeHandle(ref, () => ({
    onSubmit
  }), [onSubmit]);

  const handleChooserChange = useCallback((
    {name, value, required = false}:
    {name: BulkSelectEntitiesFormFields, value: any, required: boolean}) => {
    setFormState({[name]: {name, value: isValue(value, 'none') ? null : value, required}});
  }, []);

  return (
    <div className={classnames(classes.formWrapper, localClasses.step)}>
      <div className={classes.container}>
        <div className={classnames(localClasses.subtitle, localClasses.subtitleOffset)}>
          {shared.fieldsRequired}
        </div>
        <FormItem fullWidth>
          <IdentifierTypeChooser required
            identifiers={identifierTypes}
            entity={entity}
            hasError={formState[BulkSelectEntitiesFormFields.EntityIdentifierType]?.error}
            defaultValue={formState[BulkSelectEntitiesFormFields.EntityIdentifierType]?.value?.label}
            onChange={function(value) {
              handleChooserChange?.({
                name     : BulkSelectEntitiesFormFields.EntityIdentifierType,
                required : true,
                value    : value
              });
            }} />
        </FormItem>
        <EntityBulkUpload showFileOption={showFileOption}
          entity={entity}
          formState={formState}
          setFormState={setFormState}
          handleChooserChange={handleChooserChange}
          isBulkAction={isBulkAction} />
      </div>
    </div>
  );
});

/**
 * Determines if the entity selection details are valid to move to the next step.
 *
 * @param pastedIds the list of pasted identifiers
 * @param hasFile if there is an uploaded file
 * @param entity the entity type (device, asset, etc)
 * @returns true if details are valid, false otherwise
 */
const isValidSelection = (pastedIds: any[], hasFile: boolean, entity: BulkActionEntity): boolean => (
  hasFile || arePastedIdsValid(pastedIds, entity, false) || false
);

export default EntitySelectionStep;
